// Define the booking steps enum
export enum BookingStepEnum {
  SPECIALITIES = 'specialities',
  CONSULTATION_MODE = 'consultation-mode',
  LOCATION = 'location',
  AVAILABILITIES = 'availabilities',
  MEETING_PLACE = 'meeting-place',
  COMPANION = 'companion',
  CONSENT = 'consent',
  FORM_SUBMISSION = 'form-submission',
  PATIENT_INFORMATION = 'patient-information',
  EXPERTISE_INFORMATION = 'expertise-information',
  PAYMENT = 'payment',
  CONFIRMATION = 'confirmation',
}

export enum ConsultationMode {
  Scheduled = 'scheduled',
  Immediate = 'immediate',
}

// Patient location for territoriality compliance (ETH.XX)
export interface PatientLocation {
  postalCode: string | null
  city: string | null
  coordinates: { lat: number; lng: number } | null
  declined: boolean // Patient declined to provide location
}

// Patient consent interface for regulatory compliance (ETH.03, ETH.04, ETH.11, ETH.12, ETH.14, ETH.58)
export interface PatientConsent {
  // Required - ETH.04: Free choice confirmation
  freeChoice: boolean
  // Required - ETH.03: Information about teleconsultation act
  informedAboutTeleconsultation: boolean
  // Required: Acceptance of pricing conditions
  acceptedPricing: boolean
  // Required: Understanding of data sharing with third parties
  understoodDataSharing: boolean
  // Required - ETH.14: Acknowledgement of editor's interest links disclosure
  understoodInterestLinks: boolean
  // Optional - ETH.11, ETH.12, ETH.58: Secondary data processing consents
  dataForResearch: boolean
  dataForServiceImprovement: boolean
  // Timestamp when consents were accepted
  acceptedAt: string | null
}

export interface Companion {
  firstName: string
  lastName: string
  relationshipType: string
  relationshipTypeDisplay?: string
  email?: string
  phone?: string
  confidentialityAgreement: boolean
}

// Define the state interface
export interface BookingState {
  isLoggedIn: boolean
  meetingLinkSlug: string
  currentStep: BookingStepEnum
  consultationMode: ConsultationMode | null
  availabilityId: string | null
  selectedSpeciality: string | null
  meetingPlace: string | null
  meetingPlaceId: string | null
  meetingPlaceDescription: string | null
  meetingPlaceDetails: {
    apartment?: string | null
    floor?: string | null
    doorCode?: string | null
    instructions?: string | null
    postalCode?: string | null
    coordinates?: { lat: number; lng: number } | null
  } | null
  startDate: string | null
  formSubmissionId: string | null
  formData: Record<string, unknown>
  hasFormTemplate: boolean
  patientInfo: {
    firstName: string
    lastName: string
    email: string
    phone?: string
  }
  expertiseData: {
    comment: string
    files: Array<{ id: string; name?: string; url?: string }>
  }
  // Patient location for territoriality
  patientLocation: PatientLocation | null
  // Patient consent for regulatory compliance
  consents: PatientConsent | null
  // undefined = not visited, null = no companion, Companion = has companion
  companion?: Companion | null
  orderId: string | null
  orderReferenceId: string | null
  consultationId: string | null
  requiresPayment: boolean
  paymentCompleted: boolean
  errors: Record<string, string>
}

// Action types
export type BookingAction = (
  | { type: 'SET_IS_LOGGED_IN'; payload: boolean }
  | { type: 'SET_MEETING_LINK_SLUG'; payload: string }
  | { type: 'SET_STEP'; payload: BookingStepEnum }
  | { type: 'SET_CONSULTATION_MODE'; payload: ConsultationMode }
  | { type: 'SET_AVAILABILITY'; payload: { availabilityId: string; speciality?: string } }
  | { type: 'SET_MEETING_PLACE'; payload: { place: string; placeId: string | null; description: string } }
  | { type: 'SET_MEETING_PLACE_DETAILS'; payload: BookingState['meetingPlaceDetails'] }
  | { type: 'SET_START_DATE'; payload: string }
  | { type: 'SET_FORM_SUBMISSION'; payload: { id: string; data: Record<string, unknown> } }
  | { type: 'UPDATE_FORM_DATA'; payload: Record<string, unknown> }
  | { type: 'SET_PATIENT_INFO'; payload: BookingState['patientInfo'] }
  | { type: 'UPDATE_EXPERTISE_DATA'; payload: Partial<BookingState['expertiseData']> }
  | { type: 'SET_CONSENTS'; payload: PatientConsent }
  | { type: 'SET_PATIENT_LOCATION'; payload: PatientLocation }
  | { type: 'SET_COMPANION'; payload: Companion | null }
  | { type: 'SET_ORDER_ID'; payload: string }
  | { type: 'SET_ORDER_REFERENCE_ID'; payload: string }
  | { type: 'SET_CONSULTATION_ID'; payload: string }
  | { type: 'SET_REQUIRES_PAYMENT'; payload: boolean }
  | { type: 'SET_PAYMENT_COMPLETED'; payload: boolean }
  | { type: 'SET_HAS_FORM_TEMPLATE'; payload: boolean }
  | { type: 'SET_ERROR'; payload: { field: string; message: string } }
  | { type: 'CLEAR_ERROR'; payload: string }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'LOAD_STATE_FROM_STORAGE' }
  | { type: 'RESTORE_STATE'; payload: BookingState }
  | { type: 'RESET_STATE' }
  | { type: 'SET_CURRENT_STEP'; payload: BookingStepEnum }
) & {
  goToNextStep?: boolean
  goToPrevStep?: boolean
}

// Context value interface
export interface BookingContextValue {
  state: BookingState
  dispatch: React.Dispatch<BookingAction>
  goToNextStep: (nextState?: BookingState) => void
  goToPreviousStep: (nextState?: BookingState) => void
  goToStep: (step: BookingStepEnum, nextState?: BookingState) => void
  isStepCompleted: (step: BookingStepEnum) => boolean
  saveStateToStorage: () => void
  getStepUrl: (step: BookingStepEnum) => string
  isStepAccessible: (step: BookingStepEnum) => boolean
  getVisibleSteps: () => Array<{ key: BookingStepEnum; label: string; icon: string }>
  isLastActionableStep: (step: BookingStepEnum) => boolean
}
